import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpDown, Download, Filter, ListMusic, Plus, Search, SlidersHorizontal, Tag, Trash2, Users, X } from "lucide-react";
import Layout from "@/components/Layout";
import StickyFilters from "@/components/StickyFilters";
import ViewToggle, { useViewMode } from "@/components/ViewToggle";
import DateRangeFilter, { EMPTY_RANGE, rangeParams } from "@/components/DateRangeFilter";
import { Chips, FilterSelect, ReleasedPill, SocietyDots, SplitBar, writerLine } from "@/components/Music";
import { TrackCard, TrackDrawer, artistLine } from "@/components/Tracks";
import MergeDialog from "@/components/MergeDialog";
import { EmptyState, LoadingPanel } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useConfirm } from "@/components/Confirm";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { useTenant } from "@/context/TenantContext";
import { api, apiError, downloadFile, formatDate } from "@/lib/api";
import { cacheGet, cacheKey, cacheSet } from "@/lib/cache";
import { cn } from "@/lib/utils";

const FILTERS = [
  ["all", "All"],
  ["released", "Released"],
  ["unreleased", "Not released"],
  ["codes", "Missing codes or date"],
  ["reg", "Registration gaps"],
  ["splits", "Split problems"],
  ["dups", "Duplicate ISRC"],
];

// The server's filter rules (FILTERS in server.py). The full list is already on screen, so a chip
// narrows it in the browser at once rather than waiting for a new list.
const has = (t, codes) => (t.issues || []).some((i) => codes.includes(i.code));
const TRACK_FILTERS = {
  all: () => true,
  released: (t) => t.released === "Yes",
  unreleased: (t) => t.released !== "Yes",
  codes: (t) => has(t, ["no_upc", "no_date", "no_isrc", "bad_upc", "bad_isrc"]),
  reg: (t) => !(t.regs?.BMI?.done && t.regs?.MLC?.done),
  splits: (t) => has(t, ["split", "no_writers"]),
  dups: (t) => has(t, ["dup_isrc"]),
  problems: (t) => !!t.blocking?.length,
};

export const albumKey = (name) => (name || "").toLowerCase().replace(/riddim/g, "").replace(/[^a-z0-9]/g, "");
function toAlbumMap(d) {
  const out = {};
  for (const a of d?.albums || []) if (a.key && !out[a.key]) out[a.key] = a.album_id;
  return out;
}

const DATE_FIELDS = [
  { value: "release_date", label: "Release date" },
  { value: "original_release_date", label: "Original release" },
  { value: "created_at", label: "Date added" },
];

const SORTS = [
  { value: "release_desc", label: "Newest release" },
  { value: "release_asc", label: "Oldest release" },
  { value: "title", label: "Title A–Z" },
  { value: "artist", label: "Artist A–Z" },
  { value: "updated", label: "Recently edited" },
];

const PAGE = 100;
const COLS = "grid-cols-[28px_minmax(0,2.2fr)_minmax(0,0.9fr)_minmax(0,1fr)_minmax(0,1.1fr)_minmax(0,1.1fr)_minmax(0,1.7fr)_108px_minmax(0,0.9fr)]";

export default function Catalog() {
  const { user } = useAuth();
  const canEdit = user?.role !== "writer";
  const { tenantId } = useTenant();
  const { settings, societyLabel } = useSettings();
  const confirm = useConfirm();
  const [params, setParams] = useSearchParams();
  const [mode, setMode] = useViewMode("catalog", "list");
  const [q, setQ] = useState(params.get("q") || "");
  const [filter, setFilter] = useState(params.get("filter") || "all");
  const [artist, setArtist] = useState("");
  const [label, setLabel] = useState("");
  const [distributor, setDistributor] = useState("");
  const [range, setRange] = useState(EMPTY_RANGE);
  const [sort, setSort] = useState("release_desc");
  const writerId = params.get("writer") || "";
  const CK = cacheKey("catalog", tenantId);
  // Riddim / Album names that have an album page link to it (same matching as the server's album_key).
  const [albumByKey, setAlbumByKey] = useState(() => toAlbumMap(cacheGet(cacheKey("albums", tenantId))));
  useEffect(() => {
    if (!canEdit) return;
    api
      .get("/albums")
      .then(({ data: d }) => {
        cacheSet(cacheKey("albums", tenantId), d);
        setAlbumByKey(toAlbumMap(d));
      })
      .catch(() => {});
  }, [canEdit, tenantId]);
  const [data, setData] = useState(() => cacheGet(CK) || null);
  const [values, setValues] = useState({ artist: [], label: [], distributor: [] });
  const [writerName, setWriterName] = useState("");
  const [open, setOpen] = useState(null);
  const [limit, setLimit] = useState(PAGE);
  const [selected, setSelected] = useState(new Set());
  const reqId = useRef(0);
  const [moreFilters, setMoreFilters] = useState(false);

  const query = useMemo(
    () => ({ q, artist, label, distributor, sort, writer_id: writerId, ...rangeParams(range) }),
    [q, artist, label, distributor, sort, writerId, range]
  );

  const load = useCallback(async () => {
    const id = ++reqId.current;
    try {
      const { data: d } = await api.get("/tracks", { params: query });
      if (id !== reqId.current) return;
      setData(d);
      if (!q && !artist && !label && !distributor && !writerId && !range.from && !range.to && sort === "release_desc") cacheSet(CK, d);
    } catch (err) {
      toast.error(apiError(err));
    }
  }, [query, CK, q, artist, label, distributor, writerId, range, sort]);

  useEffect(() => {
    const t = setTimeout(load, q ? 200 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  useEffect(() => {
    api.get("/values").then(({ data: v }) => setValues(v)).catch(() => {});
  }, [tenantId]);

  useEffect(() => {
    if (!writerId) return setWriterName("");
    api.get(`/writers/${writerId}`).then(({ data: w }) => setWriterName(w.name)).catch(() => setWriterName(""));
  }, [writerId]);

  useEffect(() => {
    setLimit(PAGE);
    setSelected(new Set());
  }, [query, filter]);

  const tracks = useMemo(() => {
    const list = (data?.tracks || []).filter(TRACK_FILTERS[filter] || TRACK_FILTERS.all);
    // duplicates sit together, one group per ISRC, so each group can be merged
    return filter === "dups" ? [...list].sort((a, b) => (a.isrc || "").localeCompare(b.isrc || "") || (a.release_date || "").localeCompare(b.release_date || "")) : list;
  }, [data, filter]);
  const [merging, setMerging] = useState(null);
  const groupOf = (isrc) => tracks.filter((x) => x.isrc === isrc);
  const dupHead = (t, i, grid) =>
    filter === "dups" && (i === 0 || shown[i - 1].isrc !== t.isrc) ? (
      <div key={`h-${t.isrc}`} className={cn("flex items-center gap-3 px-3 py-2 bg-gold-light/60 border-b border-line", grid && "col-span-full rounded-xl border")}>
        <span className="text-[13px] min-w-0 flex-1">
          <b className="font-mono text-[12.5px]">{t.isrc}</b> <span className="text-muted2">· {t.title} · {groupOf(t.isrc).length} tracks</span>
          {new Set(groupOf(t.isrc).map((x) => (x.title || "").trim().toLowerCase())).size > 1 && (
            <span className="ml-2 inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold text-stock-out bg-rose-50 dark:bg-rose-950/40">Different titles — maybe a wrong ISRC</span>
          )}
        </span>
        {canEdit && (
          <Button size="sm" className="bg-brand hover:bg-brand-hover" onClick={() => setMerging(groupOf(t.isrc))}>
            Merge
          </Button>
        )}
      </div>
    ) : null;
  const counts = data?.counts || {};
  const shown = tracks.slice(0, limit);
  const current = open ? tracks.find((t) => t.track_id === open) : null;

  const toggle = (t) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(t.track_id)) n.delete(t.track_id);
      else n.add(t.track_id);
      return n;
    });
  const allShownSelected = shown.length > 0 && shown.every((t) => selected.has(t.track_id));

  const duplicate = async (t) => {
    try {
      const { data: copy } = await api.post(`/tracks/${t.track_id}/duplicate`);
      toast.success(`Copied — “${copy.title}”`);
      setOpen(copy.track_id);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const markSelected = async (society) => {
    try {
      const { data: r } = await api.post("/registrations/mark", { society, track_ids: [...selected] });
      toast.success(`${r.changed} marked as registered at ${societyLabel(society)}`);
      setSelected(new Set());
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const deleteSelected = async () => {
    const n = selected.size;
    if (!(await confirm({ title: `Delete ${n} track${n === 1 ? "" : "s"}?`, body: "This can't be undone. Export the catalog first if you want a copy.", confirmText: "Delete", destructive: true })))
      return;
    try {
      await api.post("/tracks/bulk-delete", { track_ids: [...selected] });
      toast.success("Deleted");
      setSelected(new Set());
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const clearWriter = () => {
    const p = new URLSearchParams(params);
    p.delete("writer");
    setParams(p);
  };

  const filtersOn = q || filter !== "all" || artist || label || distributor || range.from || range.to || writerId;

  return (
    <Layout
      title="Catalog"
      subtitle={data ? `${data.total} tracks · showing ${tracks.length}` : "Loading…"}
      action={
        <>
          <Button variant="outline" className="hidden sm:inline-flex" onClick={() => downloadFile("/export/catalog.xlsx", "catalog.xlsx").catch((e) => toast.error(apiError(e)))}>
            <Download className="w-4 h-4 mr-2" /> Export
          </Button>
          {canEdit && (
            <Button asChild className="bg-brand hover:bg-brand-hover">
              <Link to="/catalog/new">
                <Plus className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Add track</span>
              </Link>
            </Button>
          )}
        </>
      }
    >
      <StickyFilters className="space-y-3 -mt-3 mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <label className="relative flex-1 min-w-[200px] max-w-[300px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted2" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Title, artist, ISRC, UPC, writer…"
              aria-label="Search the catalog"
              className="w-full h-9 pl-9 pr-3 rounded-lg border border-line bg-card text-[13px] outline-none focus:border-brand-ring"
            />
          </label>
          <button
            type="button"
            onClick={() => setMoreFilters((v) => !v)}
            aria-expanded={moreFilters}
            className={cn("md:hidden inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-[13px]", artist || label || distributor || range.from || range.to ? "border-brand text-brand bg-brand-light" : "border-line bg-card")}
          >
            <SlidersHorizontal className="w-4 h-4" /> Filters
          </button>
          <div className={cn("contents", !moreFilters && "max-md:hidden")}>
            <FilterSelect value={artist} onChange={setArtist} allLabel="All artists" icon={Users} options={values.artist} />
            <FilterSelect value={label} onChange={setLabel} allLabel="All labels" icon={Tag} options={values.label.filter((v) => v.count)} />
            <FilterSelect value={distributor} onChange={setDistributor} allLabel="All distributors" icon={Filter} options={values.distributor.filter((v) => v.count)} />
            <DateRangeFilter value={range} onChange={setRange} fields={DATE_FIELDS} />
            <div className="flex-1" />
            <FilterSelect value={sort === "release_desc" ? "" : sort} onChange={(v) => setSort(v || "release_desc")} allLabel="Newest release" icon={ArrowUpDown} options={SORTS.slice(1)} ariaLabel="Sort" />
          </div>
          <ViewToggle mode={mode} onChange={setMode} />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Chips items={FILTERS.map(([key, l]) => ({ key, label: l, count: counts[key] }))} value={filter} onChange={setFilter} />
          {writerId && (
            <button onClick={clearWriter} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-sec-pub-light text-sec-pub text-[12.5px] font-semibold">
              Writer: {writerName || "…"} <X className="w-3.5 h-3.5" />
            </button>
          )}
          {filtersOn && (
            <button
              onClick={() => {
                setQ("");
                setFilter("all");
                setArtist("");
                setLabel("");
                setDistributor("");
                setRange(EMPTY_RANGE);
                clearWriter();
              }}
              className="text-[12.5px] text-muted2 hover:text-ink px-2"
            >
              Clear all
            </button>
          )}
        </div>
      </StickyFilters>

      {selected.size > 0 && canEdit && (
        <div className="sticky top-[76px] z-10 mb-3 panel px-4 py-2.5 flex items-center gap-3 flex-wrap border-brand-ring">
          <span className="text-[13px] font-semibold">{selected.size} selected</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                Mark registered at…
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuLabel>Stamps today's date</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {settings.societies.map((s) => (
                <DropdownMenuItem key={s.key} onClick={() => markSelected(s.key)}>
                  {s.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" size="sm" onClick={deleteSelected} className="text-stock-out">
            <Trash2 className="w-4 h-4 mr-1.5" /> Delete
          </Button>
          <button onClick={() => setSelected(new Set())} className="ml-auto text-[12.5px] text-muted2 hover:text-ink">
            Clear selection
          </button>
        </div>
      )}

      {!data ? (
        <LoadingPanel rows={8} />
      ) : tracks.length === 0 ? (
        <div className="panel">
          <EmptyState
            icon={ListMusic}
            title={data.total ? "Nothing matches these filters" : "Your catalog is empty"}
            body={data.total ? "Try clearing a filter or widening the dates." : "Import your spreadsheet or add your first track."}
            action={
              !data.total &&
              canEdit && (
                <div className="flex gap-2 justify-center">
                  <Button asChild variant="outline">
                    <Link to="/import">Import spreadsheet</Link>
                  </Button>
                  <Button asChild className="bg-brand hover:bg-brand-hover">
                    <Link to="/catalog/new">Add track</Link>
                  </Button>
                </div>
              )
            }
          />
        </div>
      ) : mode === "grid" ? (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
          {shown.flatMap((t, i) => [
            dupHead(t, i, true),
            <TrackCard key={t.track_id} t={t} onOpen={(x) => setOpen(x.track_id)} selected={selected.has(t.track_id)} onSelect={canEdit ? toggle : null} labels={societyLabel} />,
          ])}
        </div>
      ) : (
        <div className="panel overflow-hidden">
          <div className="overflow-x-auto">
            <div className="min-w-[1060px]">
              <div className={cn("grid gap-1.5 px-3 pt-3", COLS)}>
                <span />
                <div className="col-span-2 rounded-lg bg-sec-track-light text-sec-track font-mono text-[10.5px] tracking-[0.16em] font-semibold px-3 py-1.5">TRACK</div>
                <div className="rounded-lg bg-sec-release-light text-sec-release font-mono text-[10.5px] tracking-[0.16em] font-semibold px-3 py-1.5">RELEASE</div>
                <div className="col-span-2 rounded-lg bg-sec-codes-light text-sec-codes font-mono text-[10.5px] tracking-[0.16em] font-semibold px-3 py-1.5">CODES</div>
                <div className="rounded-lg bg-sec-pub-light text-sec-pub font-mono text-[10.5px] tracking-[0.16em] font-semibold px-3 py-1.5">PUBLISHING</div>
                <div className="rounded-lg bg-sec-mech-light text-sec-mech font-mono text-[10.5px] tracking-[0.16em] font-semibold px-3 py-1.5">MECH.</div>
                <div className="rounded-lg bg-sec-dist-light text-sec-dist font-mono text-[10.5px] tracking-[0.16em] font-semibold px-3 py-1.5">DIST.</div>
              </div>
              <div className={cn("grid gap-1.5 px-3 py-2.5 border-b border-line items-center", COLS)}>
                {canEdit ? (
                  <input
                    type="checkbox"
                    aria-label="Select all shown"
                    checked={allShownSelected}
                    onChange={() => setSelected(allShownSelected ? new Set() : new Set(shown.map((t) => t.track_id)))}
                    className="w-4 h-4 ml-1 accent-[hsl(var(--brand))]"
                  />
                ) : (
                  <span />
                )}
                {["Title / artist", "Riddim / album", "Release", "ISRC", "UPC", "Writers & splits", "B M H R", "Distributor"].map((h) => (
                  <div key={h} className="t-head px-2">
                    {h}
                  </div>
                ))}
              </div>
              {shown.flatMap((t, i) => [
                dupHead(t, i, false),
                <div
                  key={t.track_id}
                  className={cn("grid gap-1.5 px-3 py-2.5 items-center border-b border-line/70 last:border-0 hover:bg-brand-light/40 cursor-pointer", COLS, selected.has(t.track_id) && "bg-brand-light/50")}
                  onClick={() => setOpen(t.track_id)}
                >
                  {canEdit ? (
                    <input
                      type="checkbox"
                      aria-label={`Select ${t.title}`}
                      checked={selected.has(t.track_id)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggle(t)}
                      className="w-4 h-4 ml-1 accent-[hsl(var(--brand))]"
                    />
                  ) : (
                    <span />
                  )}
                  <div className="px-2 min-w-0">
                    <button className="text-[13.5px] font-semibold text-ink truncate block max-w-full text-left hover:text-brand" onClick={() => setOpen(t.track_id)}>
                      {t.title}
                    </button>
                    <div className="text-[12px] text-muted2 truncate">{artistLine(t)}</div>
                  </div>
                  <div className="px-2 text-[12.5px] text-muted2 truncate">
                    {t.riddim && albumByKey[albumKey(t.riddim)] ? (
                      <Link
                        to={`/albums/${albumByKey[albumKey(t.riddim)]}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-sec-release underline decoration-sec-release/40 underline-offset-[3px] hover:decoration-sec-release"
                        title="Open this album"
                      >
                        {t.riddim}
                      </Link>
                    ) : (
                      t.riddim || "—"
                    )}
                  </div>
                  <div className="px-2">
                    <div className="text-[12.5px] whitespace-nowrap">{t.release_date ? formatDate(t.release_date) : <span className="text-gold">No date</span>}</div>
                    <div className="mt-0.5">
                      <ReleasedPill value={t.released} />
                    </div>
                  </div>
                  <div className={cn("px-2 font-mono text-[12px] truncate", t.issues.some((i) => i.code === "dup_isrc") && "text-stock-out")}>
                    {t.isrc || <span className="text-stock-out">Missing</span>}
                  </div>
                  <div className="px-2 font-mono text-[12px] truncate">{t.upc || <span className="text-stock-out">Missing</span>}</div>
                  <div className="px-2 min-w-0">
                    <SplitBar writers={t.writers} />
                    <div className={cn("text-[12px] truncate mt-1", Math.abs(t.split_total - 100) < 0.01 ? "text-muted2" : "text-stock-out")}>{writerLine(t.writers)}</div>
                  </div>
                  <div className="px-2">
                    <SocietyDots regs={t.regs} labels={societyLabel} />
                  </div>
                  <div className="px-2 text-[12.5px] truncate">{t.distributor || "—"}</div>
                </div>,
              ])}
            </div>
          </div>
        </div>
      )}

      {tracks.length > limit && (
        <div className="mt-4 text-center">
          <Button variant="outline" onClick={() => setLimit((l) => l + PAGE)}>
            Show {Math.min(PAGE, tracks.length - limit)} more · {tracks.length - limit} left
          </Button>
        </div>
      )}
      {data && tracks.length > 0 && mode === "list" && (
        <div className="mt-3 flex items-center gap-4 text-[12px] text-muted2">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-sec-mech" /> Registered
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-surface border border-line" /> Not yet
          </span>
          <span>B = BMI · M = MLC · H = HFA · R = MR</span>
        </div>
      )}

      <MergeDialog tracks={merging || []} open={!!merging} onOpenChange={(v) => !v && setMerging(null)} onMerged={() => load()} />
      <TrackDrawer track={current} open={!!current} onOpenChange={(v) => !v && setOpen(null)} canEdit={canEdit} onDuplicate={duplicate} />
    </Layout>
  );
}

